/**
 * Gradi Schema.org Dataset `description` koji zadovoljava Google-ov zahtev
 * da bude između 50 i 5000 karaktera.
 *
 * Google Search Console error koji ovo rešava:
 *   "Invalid string length in field 'description'" (supported: [50, 5000])
 *
 * Logika:
 * 1) Strip HTML iz baznog opisa + normalize whitespace
 * 2) Spaja: description + title + Products + Markets + Year (deljeno tačkama)
 * 3) Ako je rezultat < 50 char, dodaje generički "padding" tekst
 * 4) Ako je > 5000, slice na 4997 + '...'
 *
 * Auto-import: Nuxt 3 sam importuje sve iz utils/ direktorijuma.
 */

export interface DatasetDescriptionOptions {
  /** Glavni opis iz baze (description_meta, description, cat.text, ...) */
  description?: string | null
  /** Page title za dodatni kontekst */
  title?: string | null
  /** Lista proizvoda u dataset-u (npr. iz tabele cena) */
  products?: string[]
  /** Lista pijaca/lokacija */
  markets?: string[]
  /** Godina (temporalCoverage) */
  year?: string | number | null
  /** Locale code: 'sr', 'en', 'hr', 'bs' — utiče na padding labele */
  lang?: string
}

const LABELS: Record<string, { products: string; markets: string; year: string; pad: string }> = {
  en: {
    products: 'Products',
    markets: 'Markets',
    year: 'Year',
    pad: 'Weekly market price statistics from markets across Serbia and the region.'
  },
  hr: {
    products: 'Proizvodi',
    markets: 'Tržnice',
    year: 'Godina',
    pad: 'Tjedna statistika cijena s tržnica u Hrvatskoj i regiji.'
  },
  bs: {
    products: 'Proizvodi',
    markets: 'Pijace',
    year: 'Godina',
    pad: 'Sedmična statistika cijena sa pijaca u Bosni i Hercegovini i regiji.'
  },
  // sr i sve ostalo → defaults
  default: {
    products: 'Proizvodi',
    markets: 'Pijace',
    year: 'Godina',
    pad: 'Nedeljna statistika cena sa pijaca u Srbiji i regionu.'
  }
}

function stripHtml(s: string): string {
  return s
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function buildDatasetDescription(opts: DatasetDescriptionOptions): string {
  const l = LABELS[opts.lang ?? ''] ?? LABELS.default

  const parts: string[] = []

  if (opts.description) {
    const plain = stripHtml(String(opts.description)).replace(/\.$/, '')
    if (plain) parts.push(plain)
  }

  if (opts.title) {
    const t = String(opts.title).trim().replace(/\.$/, '')
    if (t) parts.push(t)
  }

  if (Array.isArray(opts.products) && opts.products.length > 0) {
    parts.push(`${l.products}: ${opts.products.slice(0, 5).join(', ')}`)
  }

  if (Array.isArray(opts.markets) && opts.markets.length > 0) {
    parts.push(`${l.markets}: ${opts.markets.slice(0, 5).join(', ')}`)
  }

  if (opts.year != null && opts.year !== '') {
    parts.push(`${l.year}: ${opts.year}`)
  }

  let result = parts.join('. ')
  if (result && !result.endsWith('.')) result += '.'

  // Pad ako je još uvek ispod 50 karaktera (Google minimum)
  if (result.length < 50) {
    result = (result + ' ' + l.pad).trim()
  }

  // Safety cap — Google maksimum je 5000
  if (result.length > 5000) result = result.slice(0, 4997) + '...'

  return result
}
